import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  ImageList,
  ImageListItem,
  Dialog,
  DialogTitle,
  CardMedia,
  IconButton,
  useMediaQuery,
  useTheme,
  Button,
  Skeleton,
  DialogContent,
  Snackbar,
  styled,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import axiosInstance from '../../api/axiosPublic';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RainbowFunEditor from '../webadmin/WebEditor';
import { getRootSubdomain } from '../utils/subdomain';

// Placeholder image
const placeholderImage = 'https://via.placeholder.com/800x600?text=No+Image+Available';

// Modern Color Palette
const sectionColors = {
  title: '#1E3A8A',     // Deep blue
  event: '#DC2626',     // Vibrant blue
  underline: '#F59E0B', // Warm orange
  dialog: '#6B7280',    // Slate gray
  subtitle: '#475569',  // Soft slate
  backgroundGradientStart: '#F9FAFB', // Light gray
  backgroundGradientEnd: '#E2E8F0',   // Slightly darker gray
};

// Styled Components
const SectionContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${sectionColors.backgroundGradientStart} 0%, ${sectionColors.backgroundGradientEnd} 100%)`,
  minHeight: '100vh',
  padding: theme.spacing(12, 2),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(12px)', // Glassmorphism effect
    zIndex: 1,
  },
}));

const EventCard = styled(ImageListItem)(({ theme }) => ({
  borderRadius: '6px',
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '2.5rem',
  color: sectionColors.title,
  background: `linear-gradient(90deg, ${sectionColors.title}, ${sectionColors.event})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  position: 'relative',
  textAlign: 'center',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: '50%',
    bottom: -10,
    width: '15%',
    height: '4px',
    background: `linear-gradient(90deg, ${sectionColors.underline}, #FBBF24)`,
    transform: 'translateX(-50%)',
    borderRadius: 2,
    transition: 'width 0.4s ease',
  },
  '&:hover::after': {
    width: '25%',
  },
}));

// Framer Motion Variants
const fadeVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: 'easeInOut', type: 'spring', stiffness: 100 } },
};

const WebEvents = () => {
  const tenantKey = getRootSubdomain();
  const [eventsData, setEventsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogImages, setDialogImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('xs'));

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) setIsAdmin(true);
    if (!tenantKey) {
      window.location.href = '/hello';
      return;
    }
  }, [tenantKey]);

  useEffect(() => {
    if (!tenantKey) {
      setLoading(false);
      return;
    }

    axiosInstance
      .get(`/api/${tenantKey}/pages/gallery/all`)
      .then((response) => {
        let filteredEvents = response.data
          ? response.data.filter((event) => event.categoryId !== null)
          : [];
        filteredEvents.sort((a, b) => new Date(b.date) - new Date(a.date)); // Latest first
        setEventsData({ events: filteredEvents });
      })
      .catch((err) => {
        console.error('Error fetching events data:', err);
        setError('Error fetching events data.');
        setSnackbarMessage('Failed to load events data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => setLoading(false));
  }, [tenantKey]);

  const handleSave = async (updatedData) => {
    try {
      await axiosInstance.put(`/api/${tenantKey}/pages/gallery/all`, {
        content: JSON.stringify(updatedData),
      });
      setEventsData(updatedData);
      setSnackbarMessage('Events saved successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error saving events:', error);
      setSnackbarMessage('Failed to save events. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleOpenDialog = (images, index) => {
    setDialogImages(images);
    setCurrentIndex(index);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => setOpenDialog(false);
  const showPrevImage = () => setCurrentIndex((prev) => (prev === 0 ? dialogImages.length - 1 : prev - 1));
  const showNextImage = () => setCurrentIndex((prev) => (prev === dialogImages.length - 1 ? 0 : prev + 1));

  const handleOpenEditDialog = () => setIsEditDialogOpen(true);
  const handleCloseEditDialog = () => setIsEditDialogOpen(false);
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const { events = [], fallbackContent = {} } = eventsData;

  const getGridCols = () => (isSmallMobile ? 1 : isMobile ? 2 : 3);

  return (
    <SectionContainer>
      {isAdmin && (
        <Button
          onClick={handleOpenEditDialog}
          variant="contained"
          startIcon={<EditIcon />}
          sx={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 1000,
            background: `linear-gradient(90deg, ${sectionColors.underline}, #FBBF24)`,
            color: '#FFFFFF',
            borderRadius: '50px',
            px: 3,
            py: 1.5,
            fontWeight: 600,
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
            '&:hover': { background: `linear-gradient(90deg, #FBBF24, ${sectionColors.underline})` },
          }}
        >
          Edit Gallery
        </Button>
      )}

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <motion.div variants={fadeVariant} initial="hidden" animate="visible">
            <SectionTitle
              variant="h4"
              sx={{ mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}
            >
              School Event & Photo Gallery
            </SectionTitle>
            <Typography
              variant="subtitle1"
              sx={{
                color: sectionColors.subtitle,
                fontSize: '1.1rem',
                lineHeight: 1.6,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Explore moments and events captured at our school.
            </Typography>
          </motion.div>
        </Box>

        {loading ? (
          <ImageList cols={getGridCols()} gap={24}>
            {Array.from({ length: 6 }).map((_, idx) => (
              <ImageListItem key={idx}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                <Skeleton variant="text" width="60%" height={30} sx={{ mt: 1, mx: 'auto' }} />
              </ImageListItem>
            ))}
          </ImageList>
        ) : error ? (
          <Box sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          </Box>
        ) : !events.length && fallbackContent.title ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <motion.div variants={fadeVariant} initial="hidden" animate="visible">
              <Typography
                variant="h3"
                sx={{ color: sectionColors.title, mb: 3, fontWeight: 700 }}
              >
                {fallbackContent.title}
              </Typography>
              {fallbackContent.description && (
                <Typography variant="body1" sx={{ mb: 4, color: sectionColors.subtitle, lineHeight: 1.8 }}>
                  {fallbackContent.description}
                </Typography>
              )}
              {Array.isArray(fallbackContent.image) && fallbackContent.image.length > 0 && (
                <ImageList cols={getGridCols()} gap={24}>
                  {fallbackContent.image.map((imgUrl, idx) => (
                    <EventCard key={idx}>
                      <motion.img
                        src={imgUrl || placeholderImage}
                        alt="Fallback"
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                        }}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{ scale: 1.05 }}
                      />
                    </EventCard>
                  ))}
                </ImageList>
              )}
              {fallbackContent.ctaLink && fallbackContent.ctaText && (
                <Button
                  variant="contained"
                  href={fallbackContent.ctaLink}
                  sx={{
                    background: `linear-gradient(90deg, ${sectionColors.event}, ${sectionColors.title})`,
                    color: '#FFFFFF',
                    borderRadius: '50px',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    '&:hover': { background: `linear-gradient(90deg, ${sectionColors.title}, ${sectionColors.event})` },
                  }}
                >
                  {fallbackContent.ctaText}
                </Button>
              )}
            </motion.div>
          </Box>
        ) : (
          <ImageList cols={getGridCols()} gap={24}>
            {events.map((event, idx) => (
              <EventCard key={event.id}>
                <motion.div variants={cardVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                  <img
                    src={event.fileUrl || placeholderImage}
                    alt={event.eventName || `Event-${event.id}`}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleOpenDialog([event.fileUrl], 0)}
                    whileHover={{ scale: 1.05 }}
                  />
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: sectionColors.event, mb: 1 }}
                    >
                      {event.eventName || 'Event Image'}
                    </Typography>
                    {event.description && (
                      <Typography
                        variant="body2"
                        sx={{ color: sectionColors.subtitle, lineHeight: 1.5 }}
                      >
                        {event.description}
                      </Typography>
                    )}
                    {event.date && (
                      <Typography
                        variant="caption"
                        sx={{ color: sectionColors.subtitle, mt: 1, display: 'block' }}
                      >
                        {new Date(event.date).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </motion.div>
              </EventCard>
            ))}
          </ImageList>
        )}
      </Container>

      {/* Lightbox Dialog */}
      <AnimatePresence>
        {openDialog && (
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            maxWidth="lg"
            fullWidth
            sx={{ '& .MuiDialog-paper': { background: 'transparent', boxShadow: 'none' } }}
          >
            <DialogTitle sx={{ bgcolor: sectionColors.dialog, color: '#FFFFFF', p: 2, borderRadius: '16px 16px 0 0' }}>
              Event Gallery
              <IconButton
                onClick={handleCloseDialog}
                sx={{ position: 'absolute', right: 8, top: 8, color: '#FFFFFF' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 4, bgcolor: 'rgba(255, 255, 255, 0.9)', borderRadius: '0 0 16px 16px' }}>
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <CardMedia
                  component="img"
                  image={dialogImages[currentIndex] || placeholderImage}
                  sx={{
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: '6px',
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.15)',
                  }}
                  alt={`Gallery image ${currentIndex + 1}`}
                />
              </motion.div>
              {dialogImages.length > 1 && (
                <>
                  <IconButton
                    onClick={showPrevImage}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: '#FFFFFF',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                    }}
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>
                  <IconButton
                    onClick={showNextImage}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: '#FFFFFF',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </>
              )}
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 1300 }}
        TransitionComponent={motion.div}
        TransitionProps={{ variants: { hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <RainbowFunEditor
            open={isEditDialogOpen}
            onClose={handleCloseEditDialog}
            initialData={eventsData}
            onSave={handleSave}
            schoolId={tenantKey}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box
          sx={{
            bgcolor: snackbarSeverity === 'error' ? '#EF4444' : '#10B981',
            color: '#FFFFFF',
            p: 2,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '400px',
          }}
        >
          <Typography>{snackbarMessage}</Typography>
          <IconButton size="small" color="inherit" onClick={handleSnackbarClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>
    </SectionContainer>
  );
};

export default WebEvents;
